<template>
  <v-card flat dense>
    <v-form ref="form" v-model="valid">
      <v-card-title>
        <div class="d-inline-flex align-baseline">
          <span class="mr-4"
            >{{ $t('ITEM.VIEW') }} {{ $t('PLANNINGS.TASKS.EVENT.ITEM') }}</span
          >
          <span class="text-subtitle-1">
            {{ $t('PLANNINGS.TASKS.EVENT.ITEM') }}{{ $t('ITEM.ID') }}:
            {{ task.id || '' }}
          </span>
        </div>
        <v-spacer></v-spacer>
        <span class="text-body-2">{{ $t('ITEM.STATUS') }}</span>
        <div>
          <v-select
            v-model="task.status_id"
            name="status_id"
            :items="statusesList"
            item-value="id"
            item-text="name"
            small-chips
            hide-details
            dense
            flat
            solo
            style="width: 210px"
            :error-messages="errors.status_id"
            @change="clearErrorByName('status_id')"
          >
            <template v-slot:selection="{ attrs, item, select, selected }">
              <v-chip
                :color="item.color"
                :input-value="selected"
                text-color="white"
                small
                v-bind="attrs"
                @click="select"
              >
                {{ item.name }}
              </v-chip>
            </template>
          </v-select>
        </div>
        <v-btn icon @click="cancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-subtitle v-if="errorsWithoutForm.length">
        <errors :errors="errorsWithoutForm" />
      </v-card-subtitle>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <order-sheet
              v-if="task.order"
              class="pa-4"
              :order="task.order"
              :show-fields="[
                'order_number',
                'customer',
                'address',
                'mobile_phone',
                'phone',
                'email',
                'service',
                'dateServiceExpected',
                'substitution_options',
                'duration',
                'description'
              ]"
              show-title
              rounded
            ></order-sheet>
          </v-col>
          <v-col cols="12" md="8">
            <v-sheet class="pa-3" outlined rounded>
              <v-row no-gutters>
                <v-col cols="12">
                  <p class="text-h6 text--primary">
                    {{ $t('PLANNINGS.TASK_DETAILS') }}
                  </p>
                </v-col>
                <v-col cols="12">
                  <event-form
                    v-model="task"
                    :order="task.order || {}"
                    :readonly="showReportForm"
                    :errors="errors.getAll()"
                    :disable-delete="isDisableDelete"
                    @remove="removeDialog = true"
                    @update-prop="clearErrorByName($event)"
                  />
                </v-col>
              </v-row>
            </v-sheet>
          </v-col>
        </v-row>

        <v-row v-if="showReportForm">
          <v-col cols="12">
            <v-sheet class="pa-3">
              <v-row no-gutters>
                <v-col cols="12" md="8" offset-md="4">
                  <p class="text-h6 text--primary">
                    {{ $t('PLANNINGS.TASKS.EVENT.REPORT_TITLE') }}
                  </p>
                </v-col>
                <v-col cols="12" md="8" offset-md="4">
                  <event-report-form
                    v-if="isFinishedOrCancellation"
                    v-model="task"
                    :errors="errors.getAll()"
                    @update-prop="clearErrorByName($event)"
                  />

                  <event-cancelled-report-form
                    v-if="isCancelled"
                    v-model="task"
                    :errors="errors.getAll()"
                    @update-prop="clearErrorByName($event)"
                  />
                </v-col>
              </v-row>
            </v-sheet>
          </v-col>
        </v-row>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn outlined depressed color="grey" @click="cancel">
            {{ $t('BUTTON.CANCEL') }}
          </v-btn>
          <v-btn
            depressed
            color="primary"
            :disabled="!valid"
            @click="confirmUpdate"
          >
            {{ $t('BUTTON.UPDATE') }}
          </v-btn>
        </v-card-actions>
      </v-card-text>
    </v-form>
    <v-dialog v-if="removeDialog" v-model="removeDialog" max-width="500px">
      <v-card flat dense>
        <v-card-title>
          {{ $t('ITEM.DELETE') }} {{ $t('PLANNINGS.TASKS.EVENT.ITEM') }}
          <v-spacer></v-spacer>
          <v-btn icon @click="removeDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <remove-action-list
            @remove="remove"
            @close="removeDialog = !removeDialog"
          ></remove-action-list>
        </v-card-text>
      </v-card>
    </v-dialog>
    <AcceptTaskDialog
      v-if="acceptDialog"
      :show.sync="acceptDialog"
      @accept="accept"
    />
    <UpdateTaskDialog
      v-if="updateDialog"
      :show.sync="updateDialog"
      @accept="accept"
    />
    <confirm-dialog ref="confirm"></confirm-dialog>
  </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { mapCacheActions } from 'vuex-cache';
import errors from '@/modules/_global/mixins/errors';
import i18nRefreshData from '@/modules/_global/mixins/i18nRefreshData';
import { dateFormat } from '@/modules/_global/filters';
import Errors from '@/modules/_global/_components/Errors';
import OrderSheet from '../OrderSheet';
import EventForm from '../forms/Event/EventForm';
import {
  NEW,
  ACCEPTED,
  FINISHED,
  REJECTED,
  CANCELLED,
  LATE_CANCELLATION
} from '../../common/event-statuses';
import AcceptTaskDialog from '@/modules/plannings/components/Event/dialogs/AcceptTaskDialog.vue';
import UpdateTaskDialog from '@/modules/plannings/components/Event/dialogs/UpdateTaskDialog.vue';

export default {
  name: 'ViewEventTask',

  filters: {
    dateFormat
  },

  components: {
    UpdateTaskDialog,
    AcceptTaskDialog,
    Errors,
    EventForm,
    OrderSheet,
    RemoveActionList: () => import('./RemoveActionList'),
    EventReportForm: () => import('../forms/Event/EventReportForm'),
    EventCancelledReportForm: () =>
      import('../forms/Event/EventCancelledReportForm'),
    ConfirmDialog: () => import('@/modules/_global/_components/ConfirmDialog')
  },

  mixins: [errors, i18nRefreshData],

  props: {
    id: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      valid: false,
      removeDialog: false,
      acceptDialog: false,
      updateDialog: false,
      task: {}
    };
  },

  computed: {
    ...mapState('planning', ['currentTask', 'taskStatuses']),

    errorsWithoutForm() {
      return ['date_from', '_unexpected']
        .reduce((acc, i) => acc.concat(this.errors.get(i)), [])
        .filter(i => i);
    },

    showReportForm() {
      return [FINISHED, CANCELLED, LATE_CANCELLATION].includes(
        this.task.status_id
      );
    },

    isFinishedOrCancellation() {
      return (
        this.task.status_id === FINISHED ||
        this.task.status_id === LATE_CANCELLATION
      );
    },

    isCancelled() {
      return this.task.status_id === CANCELLED;
    },

    statusesList() {
      return this.taskStatuses.map(status => {
        return {
          id: status.id,
          name: status.name,
          color: status.color,
          disabled: !this.enabledStatuses.includes(status.id)
        };
      });
    },

    enabledStatuses() {
      const status = this.currentTask.status_id || NEW;

      switch (status) {
        case NEW:
          return [NEW, ACCEPTED, REJECTED, CANCELLED];
        case ACCEPTED:
          return [ACCEPTED, FINISHED, CANCELLED, LATE_CANCELLATION];
        case FINISHED:
          return [FINISHED, ACCEPTED];
        case REJECTED:
          return [REJECTED];
        case CANCELLED:
          return [CANCELLED, ACCEPTED];
        case LATE_CANCELLATION:
          return [LATE_CANCELLATION, ACCEPTED];
        default:
          return [];
      }
    },

    isDisableDelete() {
      return this.currentTask.status_id === FINISHED;
    }
  },

  mounted() {
    this.refreshData();
  },

  methods: {
    ...mapActions('planning', [
      'getTask',
      'updateTask',
      'destroyTask',
      'setIsMultiTaskUpdate'
    ]),
    ...mapCacheActions('planning', ['getTaskStatuses']),

    refreshData() {
      this.getTask({ taskId: this.id }).then(({ data }) => {
        this.task = data;
      });
      this.getTaskStatuses();
    },

    cancel() {
      this.$emit('cancel');
    },

    confirmUpdate() {
      if (
        this.currentTask.status_id === NEW &&
        this.task.status_id === ACCEPTED &&
        Boolean(this.task.is_multi_task)
      ) {
        this.acceptDialog = true;
      } else if (
        this.task.status_id === ACCEPTED &&
        Boolean(this.task.is_multi_task)
      ) {
        this.updateDialog = true;
      } else if (
        this.task.status_id === NEW &&
        Boolean(this.task.is_multi_task)
      ) {
        this.updateDialog = true;
      } else if (
        this.task.status_id === CANCELLED &&
        this.currentTask.status_id !== this.task.status_id
      ) {
        this.$refs.confirm
          .open(this.$t('PLANNINGS.TASKS.EVENT.CANCEL_QUESTION'))
          .then(confirm => {
            if (confirm) {
              this.update();
            }
          });
      } else {
        this.update();
      }
    },

    update() {
      this.updateTask({
        taskId: this.id,
        task: this.task
      })
        .then(({ data }) => {
          this.$emit('updated', { ...data.data });
        })
        .catch(({ response }) => {
          this.handleErrorsByResponse(response);
        });
    },

    remove(question) {
      this.destroyTask({
        taskId: this.id,
        question
      })
        .then(({ data }) => {
          this.$emit('removed', { ...data });
        })
        .catch(({ response }) => {
          this.handleErrorsByResponse(response);
        })
        .finally(() => {
          this.removeDialog = false;
        });
    },

    accept(question) {
      this.updateTask({
        taskId: this.id,
        task: { ...this.task, for_all_tasks: question }
      })
        .then(({ data }) => {
          this.setIsMultiTaskUpdate();
          this.$emit('updated', { ...data.data });
        })
        .catch(({ response }) => {
          this.handleErrorsByResponse(response);
        })
        .finally(() => {
          this.acceptDialog = false;
          this.updateDialog = false;
        });
    }
  }
};
</script>
